import { useEffect, useState, type FormEvent } from "react";
import * as api from "../api/client";
import { ApiError } from "../api/client";
import { usePolledResource } from "../hooks/usePolling";
import { CopyButton } from "../components/CopyButton";
import type { GeneratedClient } from "../types";

const DEFAULT_API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "https://your-worker.example.workers.dev";

function downloadLuaFile(filename: string, contents: string) {
  const blob = new Blob([contents], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function Generator() {
  const { data: info } = usePolledResource(api.getGeneratorInfo, null);

  const [displayName, setDisplayName] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState(DEFAULT_API_BASE_URL);
  const [clientVersion, setClientVersion] = useState("1.0.0");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedClient | null>(null);

  useEffect(() => {
    // apiBaseUrl defaults to wherever this dashboard is already configured
    // to talk to, since that's almost always the URL the Roblox client
    // should call too.
    if (DEFAULT_API_BASE_URL) setApiBaseUrl(DEFAULT_API_BASE_URL);
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const generated = await api.generateClient({ displayName, apiBaseUrl, clientVersion });
      setResult(generated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Generation failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="topbar">
        <div className="page-title-group">
          <h1>Generator</h1>
          <p>Produces a Roblox server script that authenticates and reports status to this deployment.</p>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h2>Configuration</h2>
          {info && (
            <span className="badge badge-neutral">
              Generator v{info.generatorVersion} · Protocol v{info.protocolVersion}
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}

          <div className="field-grid">
            <div className="form-group">
              <label htmlFor="display-name">Server display name</label>
              <input
                id="display-name"
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="EU-West Lobby 1"
                required
                maxLength={64}
              />
            </div>

            <div className="form-group">
              <label htmlFor="api-base-url">API URL</label>
              <input
                id="api-base-url"
                className="input"
                value={apiBaseUrl}
                onChange={(e) => setApiBaseUrl(e.target.value)}
                placeholder="https://your-worker.example.workers.dev"
                required
              />
              <p className="form-hint">Must be the public https:// URL of your deployed Worker.</p>
            </div>

            <div className="form-group">
              <label htmlFor="client-version">Client version</label>
              <input
                id="client-version"
                className="input"
                value={clientVersion}
                onChange={(e) => setClientVersion(e.target.value)}
                placeholder="1.0.0"
                required
                maxLength={32}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Generating…" : "Generate"}
          </button>
        </form>
      </div>

      {result && (
        <div className="panel">
          <div className="panel-header">
            <h2>Generated client</h2>
          </div>

          <div className="field-grid" style={{ marginBottom: "var(--space-4)" }}>
            <div>
              <span className="field-label">Server ID</span>
              <div className="field-value mono">{result.serverId}</div>
            </div>
            <div>
              <span className="field-label">Client version</span>
              <div className="field-value mono">{result.clientVersion}</div>
            </div>
            <div>
              <span className="field-label">Generator version</span>
              <div className="field-value mono">{result.generatorVersion}</div>
            </div>
            <div>
              <span className="field-label">Authentication status</span>
              <div className="field-value mono">{result.authenticationState}</div>
            </div>
          </div>

          <div className="form-error" style={{ background: "var(--status-degraded-bg)", borderColor: "var(--status-degraded)", color: "var(--text)" }}>
            This code contains a one-time credential for server <strong>{result.serverId}</strong>. It will not
            be shown again after you leave this page. Treat it like a secret: don't commit it to a public
            repository.
          </div>

          <div className="btn-row" style={{ marginBottom: "var(--space-3)" }}>
            <CopyButton text={result.luaCode} label="Copy code" />
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => downloadLuaFile(`${result.serverId}.server.lua`, result.luaCode)}
            >
              Download .lua
            </button>
          </div>

          <div className="code-block">{result.luaCode}</div>
        </div>
      )}
    </>
  );
}
